import type { DisplayModel } from '../display-models'

import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { refManualReset, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, watch } from 'vue'

import { DisplayModelFormat, useDisplayModelsStore } from '../display-models'

export type StageModelRenderer = 'live2d' | 'vrm' | 'disabled' | undefined

export const useSettingsStageModel = defineStore('settings-stage-model', () => {
  const displayModelsStore = useDisplayModelsStore()
  let stageModelUpdateSequence = 0
  const stageModelStorageKey = 'settings/stage/model'

  const stageModelSelectedState = useLocalStorageManualReset<string>(stageModelStorageKey, 'preset-live2d-1')
  const stageModelSelected = computed<string>({
    get: () => stageModelSelectedState.value,
    set: (value) => {
      stageModelSelectedState.value = value
    },
  })
  const stageModelSelectedDisplayModel = refManualReset<DisplayModel | undefined>(undefined)
  const stageModelSelectedUrl = refManualReset<string | undefined>(undefined)
  const stageModelRenderer = refManualReset<StageModelRenderer>(undefined)
  const stageModelUsesCharacterModel = refManualReset<boolean>(false)

  const stageViewControlsEnabled = refManualReset<boolean>(false)

  function revokeStageModelUrl(url?: string) {
    if (url?.startsWith('blob:'))
      URL.revokeObjectURL(url)
  }

  function replaceStageModelUrl(nextUrl?: string) {
    if (stageModelSelectedUrl.value === nextUrl)
      return

    revokeStageModelUrl(stageModelSelectedUrl.value)
    stageModelSelectedUrl.value = nextUrl
  }

  async function updateStageModel() {
    if (stageModelUsesCharacterModel.value && stageModelSelectedUrl.value)
      return

    const requestId = ++stageModelUpdateSequence
    const selectedModelId = stageModelSelectedState.value

    if (!selectedModelId) {
      replaceStageModelUrl(undefined)
      stageModelSelectedDisplayModel.value = undefined
      stageModelRenderer.value = 'disabled'
      return
    }

    const model = await displayModelsStore.getDisplayModel(selectedModelId)
    if (requestId !== stageModelUpdateSequence)
      return

    if (!model) {
      replaceStageModelUrl(undefined)
      stageModelSelectedDisplayModel.value = undefined
      stageModelRenderer.value = 'disabled'
      return
    }

    switch (model.format) {
      case DisplayModelFormat.Live2dZip:
        stageModelRenderer.value = 'live2d'
        break
      case DisplayModelFormat.VRM:
        stageModelRenderer.value = 'vrm'
        break
      default:
        stageModelRenderer.value = 'disabled'
        break
    }

    if (model.type === 'file') {
      const nextUrl = URL.createObjectURL(model.file)
      if (requestId !== stageModelUpdateSequence) {
        URL.revokeObjectURL(nextUrl)
        return
      }

      replaceStageModelUrl(nextUrl)
    }
    else {
      replaceStageModelUrl(model.url)
    }

    stageModelSelectedDisplayModel.value = model
  }

  async function initializeStageModel() {
    await updateStageModel()
  }

  function inferRendererFromUrl(url: string): StageModelRenderer {
    const normalized = url.toLowerCase()
    if (normalized.endsWith('.vrm'))
      return 'vrm'
    if (normalized.endsWith('.zip') || normalized.endsWith('.json'))
      return 'live2d'
    return 'disabled'
  }

  function inferFormatFromRenderer(renderer: StageModelRenderer) {
    switch (renderer) {
      case 'vrm':
        return DisplayModelFormat.VRM
      case 'live2d':
        return DisplayModelFormat.Live2dZip
      default:
        return DisplayModelFormat.Live2dZip
    }
  }

  async function applyCharacterStageModel(url?: string) {
    const normalized = url?.trim()
    if (!normalized) {
      stageModelUsesCharacterModel.value = false
      await updateStageModel()
      return
    }

    const renderer = inferRendererFromUrl(normalized)
    if (renderer === 'disabled') {
      stageModelUsesCharacterModel.value = false
      await updateStageModel()
      return
    }

    stageModelUsesCharacterModel.value = true
    replaceStageModelUrl(normalized)
    stageModelRenderer.value = renderer
    stageModelSelectedDisplayModel.value = {
      id: `character:${normalized}`,
      type: 'url',
      url: normalized,
      name: 'Character Model',
      importedAt: Date.now(),
      format: inferFormatFromRenderer(renderer),
    }
  }

  useEventListener('unload', () => {
    revokeStageModelUrl(stageModelSelectedUrl.value)
  })

  watch(stageModelSelectedState, (_newValue, _oldValue) => {
    void updateStageModel()
  })

  async function resetState() {
    revokeStageModelUrl(stageModelSelectedUrl.value)

    stageModelSelectedState.reset()
    stageModelSelectedDisplayModel.reset()
    stageModelSelectedUrl.reset()
    stageModelRenderer.reset()
    stageModelUsesCharacterModel.reset()
    stageViewControlsEnabled.reset()

    await updateStageModel()
  }

  return {
    stageModelRenderer,
    stageModelSelected,
    stageModelSelectedUrl,
    stageModelSelectedDisplayModel,
    stageModelUsesCharacterModel,
    stageViewControlsEnabled,

    initializeStageModel,
    updateStageModel,
    applyCharacterStageModel,
    resetState,
  }
})
